import {Box, Flex, HStack, IconButton, Input} from '@chakra-ui/react';
import {colors, useCartContext} from 'components/providers';
import {MinusIcon, PlusIcon, ShoppingCart, Trash2} from 'lucide-react';
import React, {FC, useEffect, useRef, useState} from 'react';

import {useToast, useTranslation} from '../../../../hooks';

interface Props {
  isSelected?: boolean;
  quantity?: number;
  massMeasurement?: string;
  onAdd(): void;
  onDelete(): void;
  isSmall?: boolean;
  cartItemId?: string;
  disabled?: boolean;
  isWeightProduct?: boolean;
}

export const ProductCartControls: FC<Props> = ({
  isSelected,
  quantity = 0,
  onAdd,
  onDelete,
  isSmall,
  cartItemId,
  disabled,
  isWeightProduct,
}) => {
  const {changeCartItemQuantity, deleteProduct} = useCartContext();
  const toast = useToast();
  const [digits, setDigits] = useState(String(quantity));
  const [prevValue, setPrevValue] = useState(quantity);
  const {t} = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const clamped = quantity > 0 ? String(quantity) : '';
    setDigits(clamped);
    if (quantity > 0) setPrevValue(quantity);
  }, [quantity]);

  const isEmpty = digits.length === 0;
  const displayValue = isEmpty ? '' : isWeightProduct ? `${digits} ${t('KG')}` : `${digits} ${t('PCS')}`;

  const placeCaret = (len: number) => {
    const el = inputRef.current;
    if (!el) return;
    try {
      el.setSelectionRange(len, len);
    } catch {}
  };

  const enforceCaret = () => placeCaret(digits.length);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(',', '.').replace(/[^\d.]/g, '');

    const regex = isWeightProduct ? /^\d+(?:\.\d{0,1})?$/ : /^\d{0,3}$/;

    if (cleaned === '' || regex.test(cleaned)) {
      const next = isWeightProduct ? cleaned : cleaned.replace(/\./g, '');
      setDigits(next);
      requestAnimationFrame(() => placeCaret(next.length));
    }
  };

  const handleFocus = () => {
    requestAnimationFrame(enforceCaret);
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    enforceCaret();
  };

  const handleSelect = () => {
    enforceCaret();
  };

  const handleBlur = () => {
    const isZeroWithDot = /^0+\.$/.test(digits);
    if (isEmpty || isZeroWithDot || Number(digits) === 0) {
      setDigits(String(prevValue));
      return;
    }

    const currentValue = Number(digits);
    if (currentValue === prevValue) return;

    if (cartItemId && currentValue > 0) {
      changeCartItemQuantity(cartItemId, +currentValue.toFixed(1));
      setPrevValue(+currentValue.toFixed(1));
      toast.success('Количество товара изменено');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const shouldDelete = isEmpty || (isWeightProduct ? Number(digits) === 0.5 : Number(digits) === 1);
    if (shouldDelete && cartItemId) {
      deleteProduct(cartItemId);
    } else {
      onDelete();
    }
  };

  return isSelected ? (
    <Box display="flex">
      <HStack _focus={{border: 'none'}} rounded="full" fontSize="18px" px={0} overflow="hidden">
        <Flex textColor={isSelected ? colors.white : ''}>
          <Box bg={isSelected ? colors.pink : colors.bodyBackground}>
            <IconButton
              icon={
                <Box display="flex" alignItems="center" justifyContent="center">
                  {isEmpty || (isWeightProduct ? Number(digits) === 0.5 : Number(digits) === 1) ? (
                    <Trash2 size={isSmall ? '16px' : undefined} />
                  ) : (
                    <MinusIcon size={isSmall ? '16px' : undefined} />
                  )}
                </Box>
              }
              size={isSmall ? '20px' : 'sm'}
              variant="unstyled"
              color="white"
              aria-label="Remove"
              boxSize={isSmall ? '25px' : '36px'}
              {...(isEmpty ? {onMouseDown: handleRemove} : {onClick: handleRemove})}
              bg="transparent"
              borderRadius="full"
            />
          </Box>

          <Input
            inputMode="numeric"
            maxLength={isWeightProduct ? 7 : 6}
            ref={inputRef}
            value={displayValue}
            textColor={colors.black}
            _hover={{borderColor: colors.pink}}
            _focus={{borderColor: colors.pink}}
            textAlign="center"
            borderRadius="0"
            borderColor={colors.pink}
            h={isSmall ? '30px' : '36px'}
            onChange={handleChange}
            onFocus={handleFocus}
            onClick={handleClick}
            onSelect={handleSelect}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />

          <Box bg={isSelected ? colors.pink : colors.bodyBackground}>
            <IconButton
              icon={
                <Box display="flex" alignItems="center" justifyContent="center">
                  <PlusIcon size={isSmall ? '16px' : undefined} />
                </Box>
              }
              size={isSmall ? '20px' : 'xs'}
              variant="unstyled"
              aria-label="Add"
              boxSize={isSmall ? '25px' : '36px'}
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
            />
          </Box>
        </Flex>
      </HStack>
    </Box>
  ) : (
    <Box display="flex" justifyContent="flex-end">
      <HStack
        bg={colors.bodyBackground}
        _focus={{border: 'none'}}
        rounded="full"
        fontSize="18px"
        px={0}
        overflow="hidden"
      >
        <Flex>
          <IconButton
            isDisabled={disabled}
            icon={
              <Box display="flex" alignItems="center" justifyContent="center">
                <PlusIcon />
              </Box>
            }
            size="xs"
            variant="unstyled"
            aria-label="Add"
            boxSize="36px"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
          />
          <IconButton
            isDisabled={disabled}
            icon={
              <Box display="flex" alignItems="center" justifyContent="center">
                <ShoppingCart />
              </Box>
            }
            size="xs"
            variant="unstyled"
            aria-label="Add"
            boxSize="36px"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
          />
        </Flex>
      </HStack>
    </Box>
  );
};
